import { useEffect, useState } from 'react';
import type { DeviceSummary } from '@aiscoreboard/contracts';
import { useT } from '../lib/i18n';
import { ApiError } from '../lib/api';
import {
  DEVICE_PAIRING_EVENT, devicePairingToken, forgetDevicePairing,
} from '../lib/devicePairing';
import {
  authorizeYouTubeCamera, getPairedYouTubeDevice, safeYouTubeWatchUrl,
  youtubeCallbackMessage, type PairedYouTubeDevice,
} from '../lib/youtube';

export function CameraYouTubeAuthorization({ device, accountToken, onChanged }: {
  device: DeviceSummary; accountToken: string; onChanged: (device: DeviceSummary) => void;
}) {
  const t = useT();
  const [token, setToken] = useState(() => devicePairingToken(device.id));
  const [status, setStatus] = useState<PairedYouTubeDevice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [callback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('device') === device.id ? params.get('youtube') : null;
  });
  const [callbackMessage] = useState(() => youtubeCallbackMessage(document.cookie, device.id));

  useEffect(() => {
    const update = () => setToken(devicePairingToken(device.id));
    window.addEventListener(DEVICE_PAIRING_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(DEVICE_PAIRING_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, [device.id]);

  useEffect(() => {
    setStatus(null);
    if (!token) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      try {
        const result = await getPairedYouTubeDevice(token, device.id, controller.signal);
        if (!controller.signal.aborted) {
          setStatus(result);
          setPollError(null);
          onChanged(result.device);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setPollError(err instanceof Error ? err.message : String(err));
          if (err instanceof ApiError && err.status === 401) forgetDevicePairing(device.id);
        }
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void poll(), 10_000);
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [token, device.id, onChanged]);

  const authorize = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const destination = await authorizeYouTubeCamera(accountToken, device.id);
      if (!destination) throw new Error(t('youtube.invalidAuthorizationUrl'));
      window.location.assign(destination);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const watchUrl = safeYouTubeWatchUrl(status?.broadcast.watchUrl ?? null);

  return <section className="space-y-3 rounded border border-white/15 p-3">
    <h2 className="font-bold">{t('youtube.title')}</h2>
    {callback === 'connected' && status?.connection && <p role="status" className="text-sm text-green-300">{t('youtube.connected')}</p>}
    {callback === 'error' && <p role="alert" className="text-sm text-red-300">{t(callbackMessage)}</p>}
    {status?.connection && <a className="block text-sm text-cyan-300 underline"
      href={`https://www.youtube.com/channel/${encodeURIComponent(status.connection.channelId)}`}
      target="_blank" rel="noopener noreferrer">{status.connection.title}</a>}
    {status && <p role="status" className="text-sm text-white/70">{t(`youtube.status.${status.broadcast.status}`)}</p>}
    {status?.broadcast.error && <p role="alert" className="text-sm text-red-300">{status.broadcast.error}</p>}
    {watchUrl && <a className="block text-sm text-cyan-300 underline" href={watchUrl}
      target="_blank" rel="noopener noreferrer">{t('youtube.watch')}</a>}
    {!device.hasStreamKey && <p className="text-sm text-white/60 max-sm:min-h-[2.5rem]">{t('youtube.saveKeyFirst')}</p>}
    {status && !status.configured && <p role="alert" className="text-sm text-red-300">{t('youtube.notConfigured')}</p>}
    {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    {pollError && <p role="alert" className="text-sm text-red-300">{pollError}</p>}
    <button type="button" className="w-full rounded bg-red-600/70 px-4 py-3 text-sm font-bold disabled:opacity-40"
      disabled={busy || status?.configured === false || !device.hasStreamKey}
      onClick={() => void authorize()}>{t(busy ? 'common.loading' : 'youtube.authorizeCamera')}</button>
  </section>;
}
